import { bytes, since } from "../board.js";
import { Brand, Calm, Certificates, Clock, DeployCard, Gauge, IncidentCard, Kpi, Label, Orb, ProjectTile, Ticker, headlineOf, toneOf } from "../parts.jsx";

/**
 * Layout 1 — Bridge.
 *
 * The operations wall as a room of them would draw it: the state of the box in
 * one line across the top, six figures under it, the fleet as a grid of tiles
 * taking most of the screen, and a column on the right for what is happening
 * now — a deployment, an incident. Resources and the recent deployments run
 * along the bottom. Everything is visible at once; nothing has to be waited
 * for.
 */
export function Bridge({ board, projects, screenKey, now }) {
    const headline = headlineOf(board);
    const h = toneOf(headline.tone);
    const { counts } = board.status;
    const active = board.deployments.active;
    const open = board.incidents.open;
    const { box } = board;
    const last = board.deployments.recent[0];

    return (
        <div className="grid h-full grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-[0.9rem]">
            <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <Brand sub="bridge" />
                <div className="flex items-center gap-4">
                    <Orb tone={headline.tone} size="2.8rem" />
                    <div>
                        <div className={`text-[1.9rem] font-semibold leading-none tracking-[0.08em] ${h.text}`}>{headline.text}</div>
                        <div className="live-label mt-1.5">
                            {counts.up}/{counts.total} answering · checked {since(board.status.checkedAt, now)} ago
                        </div>
                    </div>
                </div>
                <Clock />
            </header>

            <section className="grid grid-cols-2 gap-[0.9rem] md:grid-cols-3 xl:grid-cols-6">
                <Kpi label="projects" value={projects.size} sub={active.length ? `${active.length} deploying` : last ? `last ${since(last.endedAt, now)} ago` : "none deployed"} tone={active.length ? "running" : "unknown"} />
                <Kpi label="processes" value={box.processes.online} sub={`of ${box.processes.total} online`} tone={box.processes.stopped > 0 ? "failing" : "up"} />
                <Kpi label="answering" value={counts.up} sub={`of ${counts.total} watched`} tone={counts.down > 0 ? "down" : counts.failing > 0 ? "failing" : "up"} />
                <Kpi label="incidents" value={open.length} sub={open.length ? `oldest ${since(open.at(-1).startedAt, now)}` : "none open"} tone={open.length ? "down" : "up"} />
                <Kpi label="certificates" value={board.certificates.counts.expiring} sub={board.certificates.counts.expired ? `${board.certificates.counts.expired} expired` : board.certificates.counts.here ? `${board.certificates.counts.here} served here` : "all proxied"} tone={board.certificates.counts.expired ? "down" : board.certificates.counts.expiring ? "failing" : "up"} />
                <Kpi label="load" value={box.load?.[0] ?? null} decimals={2} sub={`${box.cores} cores`} tone={box.load?.[0] > box.cores ? "failing" : "unknown"} />
            </section>

            <section className="grid min-h-0 grid-cols-1 gap-[1.2rem] lg:grid-cols-3">
                <div className="live-card flex min-h-0 flex-col p-[1.2rem] lg:col-span-2">
                    <div className="flex items-baseline justify-between">
                        <Label>fleet</Label>
                        <span className="live-num text-[0.8rem] text-gray-600">{projects.size} projects</span>
                    </div>
                    <div className="mt-3 grid min-h-0 flex-1 auto-rows-min gap-[0.8rem] overflow-hidden [grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))]">
                        {[...projects.values()].map((project) => (
                            <ProjectTile key={project.slug} project={project} screenKey={screenKey} now={now} />
                        ))}
                    </div>
                </div>

                <div className="flex min-h-0 flex-col gap-[1.2rem]">
                    <div className="flex min-h-0 flex-1 flex-col gap-[0.8rem] overflow-hidden">
                        <Label>now</Label>
                        {active.length ? (
                            active.map((deployment, i) => <DeployCard key={deployment.id} deployment={deployment} project={projects.get(deployment.slug)} screenKey={screenKey} compact={i > 0} rows={3} siblings={active} />)
                        ) : (
                            <div className="live-card flex-1">
                                <Calm title="Nothing deploying" detail={last ? `last: ${last.slug} #${last.number} · ${last.status} · ${since(last.endedAt, now)} ago` : null} className="h-full" />
                            </div>
                        )}
                    </div>
                    <div className={`flex flex-col gap-[0.8rem] overflow-hidden ${open.length ? "shrink-0" : "min-h-0 flex-1"}`}>
                        <div className="flex items-baseline justify-between">
                            <Label>incidents</Label>
                            {open.length > 3 ? <span className="live-num text-[0.8rem] text-red-400">+{open.length - 3} more</span> : null}
                        </div>
                        {open.length ? (
                            open.slice(0, 3).map((incident) => <IncidentCard key={incident.id} incident={incident} />)
                        ) : (
                            <div className="live-card flex-1">
                                <Calm title="No open incidents" detail={board.incidents.recent[0] ? `last: ${board.incidents.recent[0].name} · ${since(board.incidents.recent[0].endedAt, now)} ago` : null} className="h-full" />
                            </div>
                        )}
                        <Certificates certificates={board.certificates} />
                    </div>
                </div>
            </section>

            <footer className="grid grid-cols-1 gap-[1rem] md:grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_2.2fr]">
                <Gauge label="cpu" ratio={(box.cpu ?? 0) / 100} text={box.cpu === null ? "—" : `${Math.round(box.cpu)}%`} sub={`load ${box.load?.[0]?.toFixed(2) ?? "—"}`} trace={{ values: box.history.cpu, max: 100 }} />
                <Gauge label="memory" ratio={box.memory.total ? box.memory.used / box.memory.total : 0} text={box.memory.total ? `${Math.round((box.memory.used / box.memory.total) * 100)}%` : "—"} sub={`${bytes(box.memory.used)} / ${bytes(box.memory.total)}`} trace={{ values: box.history.memory, max: box.memory.total }} />
                <Gauge label="disk" ratio={box.disk.total ? box.disk.used / box.disk.total : 0} text={box.disk.total ? `${Math.round((box.disk.used / box.disk.total) * 100)}%` : "—"} sub={`${bytes(box.disk.total ? box.disk.total - box.disk.used : null)} free`} />
                <div className="live-card flex flex-col justify-center gap-2 p-[1rem] md:col-span-3 lg:col-span-1">
                    <Label>recent deployments</Label>
                    <Ticker deployments={board.deployments.recent} projects={projects} screenKey={screenKey} />
                </div>
            </footer>
        </div>
    );
}
